/**
 * shared/useApi.js
 *
 * FIXES:
 *  - BUG-008 (stale closure): immediate=true com funções inline causava
 *    loop infinito porque execute recriar quando onSuccess/onError mudavam.
 *    Fix: usar refs para callbacks, remover das deps do useCallback.
 *  - Adicionado retry automático (configurable) para falhas de rede
 *  - Adicionado timeout de abort respeitado
 */

import { useState, useCallback, useRef, useEffect } from 'react';

export const useApi = (apiFunction, options = {}) => {
  const {
    immediate    = false,
    initialData  = null,
    onSuccess,
    onError,
    retries      = 0,       // nº de retries em caso de erro de rede
    retryDelay   = 1000,    // ms entre retries
  } = options;

  const [data,      setData]      = useState(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [error,     setError]     = useState(null);

  const abortRef      = useRef(null);
  const mountedRef    = useRef(true);
  // FIX: refs para callbacks evitam que execute seja recriado a cada render
  const onSuccessRef  = useRef(onSuccess);
  const onErrorRef    = useRef(onError);
  const apiFnRef      = useRef(apiFunction);

  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);
  useEffect(() => { onErrorRef.current   = onError;   }, [onError]);
  useEffect(() => { apiFnRef.current     = apiFunction; }, [apiFunction]);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const execute = useCallback(async (...args) => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    if (mountedRef.current) {
      setIsLoading(true);
      setError(null);
    }

    let attempt = 0;
    while (attempt <= retries) {
      try {
        const result = await apiFnRef.current(...args);
        if (mountedRef.current) {
          setData(result);
          onSuccessRef.current?.(result);
        }
        return result;
      } catch (err) {
        if (err.name === 'AbortError') return;

        const isLastAttempt = attempt >= retries;
        const isNetworkError = !err.response; // axios sem resposta = rede

        if (!isLastAttempt && isNetworkError) {
          attempt++;
          const espera = retryDelay * attempt;
          await new Promise(res => setTimeout(res, espera));
          continue;
        }

        const message = err.response?.data?.message || err.message || 'Erro desconhecido';
        if (mountedRef.current) {
          setError(message);
          onErrorRef.current?.(err);
        }
        throw err;
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    }
  }, [retries, retryDelay]); // FIX: deps estáveis — não recriar por causa de callbacks

  // FIX: immediate não causa loop — execute é estável
  useEffect(() => {
    if (immediate) execute().catch(() => {});
    return () => abortRef.current?.abort();
  }, [immediate, execute]);

  const refresh = useCallback(() => execute(), [execute]);
  const reset   = useCallback(() => {
    setData(initialData);
    setError(null);
    setIsLoading(false);
  }, [initialData]);

  return { data, isLoading, error, execute, refresh, reset };
};

// ─── Utilitários extra ────────────────────────────────────────
export { useDebounce, useLocalStorage } from './useApiUtils';
